import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import logo from '@/assets/logo.webp';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const email = (e.target as any).email.value;
    const password = (e.target as any).password.value;

    try {
      // Make the API call to authenticate the user
      const response = await fetch(`${import.meta.env.VITE_ADMIN_AUTH_ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error("Invalid credentials");
      }

      // Parse the response body (assuming it's JSON)
      const data = await response.json();

      const user = jwtDecode(data.token, { header: false }) as {
        sub: string;
        given_name: string;
        family_name: string;
        email: string;
        phone: string;
        studioName: string;
      };

      const adminUser = {
        id: user.sub,
        firstName: user.given_name,
        lastName: user.family_name,
        email: user.email,
        studioName: user.studioName,
        phone: user.phone,
      }

      const userData = {
        id: user.sub,
        email: adminUser.email,
        firstName: adminUser.firstName,
        lastName: adminUser.lastName,
        token: data.token,
      };

      window.localStorage.setItem('currentUser', JSON.stringify(adminUser));
      window.sessionStorage.setItem(user.sub, data.token);

      setUser(userData);

      // Redirect to the dashboard after successful login
      navigate("/admin/dashboard");

    } catch (error) {
      console.error("Authentication failed:", error);
      alert(t("authDialog.loginFailed"));
    }
  };

  useEffect(() => {
    document.title = `${t('login.title')} - FitCalendar`;
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md ">
        <div className="flex flex-col items-center">
           <a href="/"><img src={logo} alt="FitCalendar" className="h-6 object-contain mt-8 mb-8" /></a>
        </div>
        <CardHeader>
          <CardTitle>{t('login.title')}</CardTitle>
          <CardDescription>
            {t('login.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">{t('login.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('login.emailPlaceholder')}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('login.password')}</Label>
              <Input id="password" type="password" required />
            </div>
            <Button type="submit" className="w-full">
              {t('login.signIn')}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <Link to="/admin/forgot-password" className="text-sm text-blue-500 hover:underline">
              {t("login.forgotPassword")}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;